import React, { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Archive, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import HistoryFilterBar from "../components/history/HistoryFilterBar";
import HistoryTimeline from "../components/history/HistoryTimeline";
import HistoryAddNoteDialog from "../components/history/HistoryAddNoteDialog";

const PAGE_SIZE = 20;

const MemberHistoryLogPanel = ({ user }) => {
  const todayStr = new Date().toLocaleDateString("en-CA");
  const isSuperadmin = user?.role === "superadmin";

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state (editing values)
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("all");
  const [actorId, setActorId] = useState("all");
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  // Applied state (drives the fetch)
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedAction, setAppliedAction] = useState("all");
  const [appliedActorId, setAppliedActorId] = useState("all");
  const [appliedStart, setAppliedStart] = useState(todayStr);
  const [appliedEnd, setAppliedEnd] = useState(todayStr);

  const [filterOptions, setFilterOptions] = useState({ actors: [], actions: [] });

  // Add note dialog
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteMemberId, setNoteMemberId] = useState("");
  const [noteText, setNoteText] = useState("");

  const fetchFilterOptions = useCallback(async () => {
    try {
      const resp = await axiosInstance.get("/member-history/filters");
      setFilterOptions(resp.data || { actors: [], actions: [] });
    } catch (e) {
      console.error("Failed to fetch filter options", e);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (appliedSearch) params.append("search", appliedSearch);
      if (appliedAction !== "all") params.append("action", appliedAction);
      if (appliedActorId !== "all") params.append("actor_id", appliedActorId);
      if (appliedStart) params.append("start_date", new Date(appliedStart + "T00:00:00").toISOString());
      if (appliedEnd) params.append("end_date", new Date(appliedEnd + "T23:59:59").toISOString());
      const resp = await axiosInstance.get(`/member-history?${params.toString()}`);
      setLogs(resp.data.logs || []);
      setTotalCount(resp.data.total || 0);
      setTotalPages(resp.data.total_pages || 0);
    } catch (e) {
      console.error("Failed to fetch member history", e);
      toast.error("Gagal memuat history log");
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch, appliedAction, appliedActorId, appliedStart, appliedEnd]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleApplyFilter = () => {
    setPage(1);
    setAppliedSearch(search);
    setAppliedAction(action);
    setAppliedActorId(actorId);
    setAppliedStart(startDate);
    setAppliedEnd(endDate);
  };

  const handleResetFilter = () => {
    setSearch("");
    setAction("all");
    setActorId("all");
    setStartDate("");
    setEndDate("");
    setAppliedSearch("");
    setAppliedAction("all");
    setAppliedActorId("all");
    setAppliedStart("");
    setAppliedEnd("");
    setPage(1);
  };

  const handlePreset = (preset) => {
    const today = new Date();
    let s = new Date();
    let e = new Date();
    switch (preset) {
      case "today":
        s = e = new Date();
        break;
      case "yesterday":
        s = e = new Date(today.getTime() - 86400000);
        break;
      case "this_week": {
        const dow = today.getDay();
        const offset = dow === 0 ? -6 : 1 - dow;
        s = new Date(today);
        s.setDate(today.getDate() + offset);
        e = new Date(today);
        break;
      }
      case "this_month":
        s = new Date(today.getFullYear(), today.getMonth(), 1);
        e = new Date(today);
        break;
      default:
        break;
    }
    const fmt = (d) => d.toLocaleDateString("en-CA");
    const ss = fmt(s);
    const ee = fmt(e);
    setStartDate(ss);
    setEndDate(ee);
    setAppliedStart(ss);
    setAppliedEnd(ee);
    setPage(1);
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteMemberId.trim() || !noteText.trim()) {
      toast.error("Member ID dan catatan wajib diisi");
      return;
    }
    try {
      await axiosInstance.post("/member-history/note", {
        member_id: noteMemberId.trim(),
        note: noteText.trim(),
      });
      toast.success("Catatan berhasil ditambahkan");
      setShowNoteDialog(false);
      setNoteMemberId("");
      setNoteText("");
      fetchLogs();
      fetchFilterOptions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menambahkan catatan");
    }
  };

  const handleArchive = async () => {
    if (!window.confirm("Arsipkan & kirim log lebih dari 1 tahun ke email situkangsulap@gmail.com? Log yang berhasil dikirim akan dihapus dari database.")) return;
    setArchiving(true);
    try {
      const resp = await axiosInstance.post("/member-history/archive");
      const { archived_count, email_sent } = resp.data || {};
      if (archived_count === 0) {
        toast.info("Tidak ada log lebih dari 1 tahun untuk diarsipkan");
      } else if (email_sent) {
        toast.success(`${archived_count} entri diarsipkan & dikirim ke email`);
      } else {
        toast.error(`Gagal kirim email — ${archived_count} entri TIDAK dihapus, masih aman`);
      }
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengarsipkan log");
    } finally {
      setArchiving(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm("Hapus entri history ini permanen?")) return;
    try {
      await axiosInstance.delete(`/member-history/${entryId}`);
      toast.success("Entri history dihapus");
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus entri");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0f10] via-[#1a1a1b] to-[#0f0f10]" data-testid="history-log-page">
      <header className="bg-[#1a1a1b]/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-20">
        <div className="px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-red-50 pl-12 lg:pl-0">Member History Log</h1>
          <div className="flex items-center gap-2">
            <HistoryAddNoteDialog
              open={showNoteDialog}
              onOpenChange={setShowNoteDialog}
              memberId={noteMemberId}
              setMemberId={setNoteMemberId}
              noteText={noteText}
              setNoteText={setNoteText}
              onSubmit={handleAddNote}
            />
            {isSuperadmin && (
              <Button
                onClick={handleArchive}
                disabled={archiving}
                variant="outline"
                className="border-yellow-700/40 text-yellow-300 hover:bg-yellow-900/20"
                data-testid="archive-btn"
                title="Arsipkan log lebih dari 1 tahun & kirim ke email"
              >
                <Archive size={16} className="mr-2" />
                {archiving ? "Mengarsipkan..." : "Arsipkan >1 Tahun"}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
        <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30 mb-6">
          <CardHeader>
            <CardTitle className="text-red-50 text-base">Filter</CardTitle>
            <CardDescription className="text-gray-400">
              Default menampilkan aktivitas hari ini. Ubah filter lalu klik <b>Terapkan</b>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <HistoryFilterBar
              search={search}
              setSearch={setSearch}
              action={action}
              setAction={setAction}
              actorId={actorId}
              setActorId={setActorId}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              filterOptions={filterOptions}
              totalCount={totalCount}
              onApply={handleApplyFilter}
              onReset={handleResetFilter}
              onPreset={handlePreset}
            />
          </CardContent>
        </Card>

        <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30">
          <CardContent className="pt-6">
            <HistoryTimeline
              logs={logs}
              loading={loading}
              isSuperadmin={isSuperadmin}
              onDelete={handleDeleteEntry}
            />

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} variant="outline" size="sm" className="border-red-900/30 text-white">
                  Sebelumnya
                </Button>
                <span className="text-sm text-gray-400 px-3">Halaman {page} dari {totalPages}</span>
                <Button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} variant="outline" size="sm" className="border-red-900/30 text-white">
                  Selanjutnya
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {isSuperadmin && (
          <div className="mt-4 p-3 rounded-lg bg-yellow-900/10 border border-yellow-700/30 text-xs text-yellow-200 flex gap-2">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <div>
              Log otomatis diarsipkan setiap hari pukul 02:00 UTC. Entri lebih dari 1 tahun dikirim ke <b>situkangsulap@gmail.com</b> sebagai CSV sebelum dihapus dari database.
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MemberHistoryLogPanel;
